import { GetStaticPathsResult, GetStaticPropsContext } from 'next';

import { getBlogPostsBySpecies } from '../../../../../src/api/contentful/blog/getBlogPostsBySpecies';
import { getSpeciesById } from '../../../../../src/api/contentful/blog/getSpeciesById';
import { getRutilusGraphqlClient } from '../../../../../src/api/graphql/getRutilusGraphqlClient';
import { queryRutilusStatic } from '../../../../../src/api/graphql/queryRutilusStatic';
import { AppLogic } from '../../../../../src/components/AppLogic/AppLogic';
import { GlobalLayout } from '../../../../../src/components/Layouts/GlobalLayout';
import { BLOG_PAGE_NAV_LINKS, BLOG_POSTS_PER_PAGE } from '../../../../../src/constants/constants';
import { NOT_FOUND_WITH_REVALIDATION, REVALIDATE } from '../../../../../src/constants/next';
import { reportMykissError } from '../../../../../src/helpers/errorHandling';
import { isExternalId } from '../../../../../src/helpers/strings/isExternalId';
import { toNumber } from '../../../../../src/helpers/strings/toNumber';
import {
  getBlogPostsUrl,
  getSpeciesBlogPostsUrl,
  getSpeciesUrl,
} from '../../../../../src/helpers/urls';
import { GetSingleSpecies, GetSingleSpeciesVariables } from '../../../../../src/interfaces/graphql';
import { StaticResult } from '../../../../../src/interfaces/nextjs';
import { getSingleSpecies } from '../../../../../src/queries/rutilus/Species';
import { IBlogPostsProps } from '../../../../../src/views/Blog/BlogPosts/BlogPosts';
import BlogPosts from '../../../[categorySlug]';
import { getSpeciesBySlug } from '../../../../../src/api/contentful/blog/getSpeciesBySlug';

type IParams = {
  speciesSlug: string;
  page: string;
};

export async function getStaticProps({
  params,
}: GetStaticPropsContext<IParams>): StaticResult<IBlogPostsProps> {
  const rutilusClient = getRutilusGraphqlClient({ serverMode: 'static' });
  const page = toNumber(params?.page);
  const speciesSlug = params?.speciesSlug;

  if (!speciesSlug || !page) {
    return { notFound: true };
  }

  try {
    const contentfulResultBySlug = await getSpeciesBySlug(speciesSlug);
    // species slug does not exist in contentful
    if (!contentfulResultBySlug) {
      // legacy redirect for urls with external ids instead of slugs
      if (isExternalId(speciesSlug)) {
        const speciesId = speciesSlug;
        const contentfulResultById = await getSpeciesById(speciesId);
        // species exists in contentful, redirect to species blog posts slug url
        if (contentfulResultById?.fields.slug) {
          return {
            redirect: {
              destination: getBlogPostsUrl(
                page,
                getSpeciesBlogPostsUrl(contentfulResultById.fields.slug),
              ),
              permanent: true,
            },
          };
        }
        // species exists in rutilus
        const { response: rutilusResult } = await queryRutilusStatic<
          GetSingleSpecies,
          GetSingleSpeciesVariables
        >(rutilusClient, {
          query: getSingleSpecies,
          variables: { id: speciesId },
        });
        // add temporary redirect to rutilus species page
        // since the species id is correct and might be added to contentful later
        if (rutilusResult) {
          return {
            redirect: {
              destination: getSpeciesUrl(speciesId, rutilusResult?.data.entry?.displayName),
              permanent: false,
            },
          };
        }
      }
      // species id has correct format but does not exist in rutilus
      return NOT_FOUND_WITH_REVALIDATION;
    }

    const species = contentfulResultBySlug.fields;
    const contentfulSpeciesId = contentfulResultBySlug.sys.id;

    const { items, total } = await getBlogPostsBySpecies(
      page,
      BLOG_POSTS_PER_PAGE,
      contentfulSpeciesId,
    );

    if (page > Math.ceil(total / BLOG_POSTS_PER_PAGE)) {
      return {
        redirect: {
          destination: getSpeciesBlogPostsUrl(species.externalId),
          permanent: false,
        },
      };
    }

    return {
      props: {
        _page: 'blog_species',
        _pageTitle: 'Blog Species',
        posts: items,
        page,
        endPage: Math.ceil(total / BLOG_POSTS_PER_PAGE),
        canonicalPath: getBlogPostsUrl(page, getSpeciesBlogPostsUrl(species.slug)),
        title: species.name,
        type: 'species',
        paginationPath: getSpeciesBlogPostsUrl(species.slug),
      },
      revalidate: REVALIDATE.HOURLY,
    };
  } catch (error) {
    reportMykissError(error);
    return NOT_FOUND_WITH_REVALIDATION;
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult<IParams>> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

BlogPosts.getLayout = function getLayout(page: any) {
  return (
    <AppLogic pageProps={page.props}>
      <GlobalLayout siteSubNavigationLinks={BLOG_PAGE_NAV_LINKS}>{page}</GlobalLayout>
    </AppLogic>
  );
};

export default BlogPosts;
